import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Alert } from '@/components/ui/alert';

interface CadViewerBannersProps {
  error: string | null;
  onDismissError: () => void;
  drbfmSessionId: string | null;
  drbfmLoading: boolean;
  drbfmError: string | null;
  /** Total markers returned for the session. */
  totalMarkers: number;
  /** Markers anchored to the model currently on screen. */
  shownMarkers: number;
  /** Distinct CAD locations those shown markers group into. */
  locationCount: number;
}

/**
 * The page error alert plus the DRBFM session linkage banners. Markup is
 * unchanged from the inline block it replaced.
 */
const CadViewerBanners: React.FC<CadViewerBannersProps> = ({
  error,
  onDismissError,
  drbfmSessionId,
  drbfmLoading,
  drbfmError,
  totalMarkers,
  shownMarkers,
  locationCount,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {error && (
        <Alert variant="destructive">
          <div className="flex items-start justify-between gap-3">
            <span>{error}</span>
            <button
              type="button"
              aria-label={t('common.close')}
              onClick={onDismissError}
              className="shrink-0 rounded-sm text-current opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </button>
          </div>
        </Alert>
      )}

      {/* DRBFM session linkage banner (TG #673) */}
      {drbfmSessionId && drbfmLoading && (
        <Alert variant="info">{t('cad.drbfmLoading')}</Alert>
      )}
      {drbfmSessionId && drbfmError && (
        <Alert variant="warning">
          {t('cad.drbfmLoadFailed', { message: drbfmError })}
        </Alert>
      )}
      {drbfmSessionId && !drbfmLoading && !drbfmError && totalMarkers > 0 && (
        <Alert variant={shownMarkers > 0 ? 'success' : 'warning'}>
          {shownMarkers > 0 ? (
            t('cad.drbfmLinked', {
              shown: shownMarkers,
              total: totalMarkers,
              locations: locationCount,
            })
          ) : (
            t('cad.drbfmNoMatchingModel', { total: totalMarkers })
          )}
        </Alert>
      )}
    </>
  );
};

export default CadViewerBanners;
